/**
 * PatrolLocationMarker
 *
 * Clickable marker rendered at the center of each patrol location circle.
 * Provides reliable click detection since Google Maps Circle clicks are unreliable.
 *
 * Color-coded based on patrol status:
 * - Green: Guard is on-site (within 50m)
 * - Orange: Guard is assigned but away from site
 * - Blue: Patrol completed
 * - Grey: No guard assigned / Pending
 */

import React, { memo } from 'react';
import { Tooltip } from '@mui/material';
import PlaceIcon from '@mui/icons-material/Place';
import classNames from 'classnames';
import styles from './PatrolLocationMarker.module.css';
import { PatrolLocation, PATROL_CIRCLE_STATUS_LABELS } from './types';

interface PatrolLocationMarkerProps {
  /** Patrol location data */
  patrol: PatrolLocation;
  /** Whether this marker is currently selected */
  isSelected?: boolean;
  /** Callback when marker is clicked */
  onClick?: (patrol: PatrolLocation) => void;
  /** Required by google-map-react for positioning */
  lat: number;
  /** Required by google-map-react for positioning */
  lng: number;
  /** Optional sequence number to display (1, 2, 3...) for guard-filtered view */
  sequenceNumber?: number;
  /** Override patrol status for coloring in guard-filtered view */
  filteredPatrolStatus?: 'pending' | 'in_progress' | 'completed';
}

const PatrolLocationMarker: React.FC<PatrolLocationMarkerProps> = ({
  patrol,
  isSelected = false,
  onClick,
  sequenceNumber,
  filteredPatrolStatus,
}) => {
  const handleClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    onClick?.(patrol);
  };

  // Determine if we're in numbered/filtered mode
  const isNumbered = sequenceNumber !== undefined;

  // For numbered markers, use patrol status for coloring
  // For regular markers, use circle status (guard proximity)
  let statusClass: string;
  if (isNumbered && filteredPatrolStatus) {
    statusClass = {
      pending: styles.patrolStatusPending,
      in_progress: styles.patrolStatusInProgress,
      completed: styles.patrolStatusCompleted,
    }[filteredPatrolStatus];
  } else {
    statusClass = {
      guard_inside: styles.statusGuardInside,
      guard_outside: styles.statusGuardOutside,
      no_guard: styles.statusNoGuard,
      completed: styles.statusCompleted,
      pending: styles.statusPending,
    }[patrol.circleStatus];
  }

  // Determine the status label for tooltip
  const statusLabel = isNumbered && filteredPatrolStatus
    ? { pending: 'Not Started', in_progress: 'In Progress', completed: 'Completed' }[filteredPatrolStatus]
    : PATROL_CIRCLE_STATUS_LABELS[patrol.circleStatus];

  const tooltipContent = (
    <div>
      {isNumbered && <strong>#{sequenceNumber} - </strong>}
      <strong>{patrol.siteName}</strong>
      <br />
      {patrol.customerName}
      <br />
      <em>{statusLabel}</em>
      {patrol.guardName && (
        <>
          <br />
          Guard: {patrol.guardName}
        </>
      )}
    </div>
  );

  // Check if two-digit number
  const isTwoDigit = sequenceNumber !== undefined && sequenceNumber >= 10;

  return (
    <Tooltip title={tooltipContent} arrow placement="top">
      <div
        className={classNames(styles.marker, statusClass, {
          [styles.selected]: isSelected,
          [styles.numbered]: isNumbered,
          [styles.twoDigit]: isTwoDigit,
        })}
        onClick={handleClick}
      >
        {isNumbered ? (
          <span>{sequenceNumber}</span>
        ) : (
          <PlaceIcon className={styles.icon} />
        )}
      </div>
    </Tooltip>
  );
};

/**
 * Custom comparison function for React.memo
 * Only re-render when values that actually affect the marker change
 */
function arePropsEqual(
  prevProps: PatrolLocationMarkerProps,
  nextProps: PatrolLocationMarkerProps
): boolean {
  // Compare selection state
  if (prevProps.isSelected !== nextProps.isSelected) {
    return false;
  }

  // Compare sequence number (for guard-filtered view)
  if (prevProps.sequenceNumber !== nextProps.sequenceNumber) {
    return false;
  }

  // Compare filtered patrol status
  if (prevProps.filteredPatrolStatus !== nextProps.filteredPatrolStatus) {
    return false;
  }

  // Compare patrol data that affects rendering (by value, not reference)
  const prevPatrol = prevProps.patrol;
  const nextPatrol = nextProps.patrol;

  if (
    prevPatrol.addressId !== nextPatrol.addressId ||
    prevPatrol.siteName !== nextPatrol.siteName ||
    prevPatrol.customerName !== nextPatrol.customerName ||
    prevPatrol.circleStatus !== nextPatrol.circleStatus ||
    prevPatrol.guardName !== nextPatrol.guardName ||
    prevPatrol.patrolStatus !== nextPatrol.patrolStatus
  ) {
    return false;
  }

  // onClick callback reference changes don't require re-render
  // (handler captures current patrol via closure)

  return true;
}

export default memo(PatrolLocationMarker, arePropsEqual);
